import {
  Alert,
  AlertIcon,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useCreatePreassignedInternalLoanTapesFromManualAssignmentMutation } from 'packages/loanProduct/requests/requests.gql.generated';

type Props = {
  getSignaledAssignments: () => {
    finleyReceivableId: string;
    creditFacilityId: string;
  }[];
  onConfirm: () => void;
  currentMasterLoanTapeIds: string[];
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  disabled?: boolean;
};
export const AssignmentEditConfirmButton = ({
  getSignaledAssignments,
  onConfirm,
  currentMasterLoanTapeIds,
  reportingDate,
  fileVersion,
  disabled,
}: Props) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [
    createPreassignedInternalLoanTapesFromManualAssignment,
    { loading },
  ] = useCreatePreassignedInternalLoanTapesFromManualAssignmentMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const cancelRef = React.useRef(null);

  return (
    <>
      <Button
        colorScheme="green"
        aria-label="confirm assignment"
        isLoading={loading}
        onClick={onOpen}
        disabled={!!disabled}
      >
        Confirm Assignment
      </Button>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={isOpen}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm assignment
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb="10px">
                Are you sure you would like to confirm assignment for the
                following?
              </Text>
              <Alert status="warning" mb="20px" mt="20px">
                <AlertIcon />
                <AlertDescription>
                  Receivable assignments cannot be altered after confirmation.
                </AlertDescription>
              </Alert>
              <Text>
                <strong>Reporting date: </strong>
                {commonDate.fullDateStrToHumanDateString(reportingDate)}
              </Text>
              <Text>
                <strong>File version: </strong>
                {fileVersion}
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef.current} onClick={onClose}>
                Cancel
              </Button>
              <Button
                isLoading={loading}
                colorScheme="green"
                onClick={async () => {
                  try {
                    const signaledAssignments = getSignaledAssignments();

                    const {
                      data,
                    } = await createPreassignedInternalLoanTapesFromManualAssignment(
                      {
                        variables: {
                          masterLoanTapeIds: currentMasterLoanTapeIds,
                          signaledAssignments,
                        },
                      },
                    );

                    onClose();
                    onConfirm();

                    if (data) {
                      successToastConfig.title =
                        'Successfully submitted assignments. Please check your email for confirmation.';
                      toast(successToastConfig);
                      navigate('/');
                    }
                  } catch (err: any) {
                    if (!toast.isActive(errorToastId)) {
                      errorToastConfig.title = `Error confirming assignment`;
                      toast(errorToastConfig);
                    }
                    onClose();
                    throw err;
                  }
                }}
                ml={3}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
